// dependencies
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { AnyAction } from "redux";

import { faArchive, faCheck, faPlus } from "@fortawesome/pro-solid-svg-icons";

//components
import DownloadButton from "../../../../../components.smart/DownloadButton/DownloadButton";
import Button from "../../../../../components/Button";
import ThumbSwitch from "../../../../../components/Inputs/ToggleSwitches/ThumbSwitch";
import Pagination from "../../../../../components/Pagination";
import { SideNavigationLink } from "../../../../../components/SideNavigation";
import Spacer from "../../../../../components/Spacer";
import TableBulk from "../../../../../components/TableLite/TableLiteBulk";
import TableScrollable from "../../../../../components/TableLite/TableLiteScrollable";
import Typography from "../../../../../components/Text";
import ArchiveGoalModal from "./components/ArchiveGoalsModal";
import CompleteGoalsModal from "./components/CompleteGoalsModal";
import GoalsListFilters, { GoalsFilters } from "./components/Filters";
import getGoalsListCols from "./components/GoalsListCols";
import ReadableGoalsFilterChips from "./components/ReadableFiltersList";

//hooks
import {
	SortDirection,
	useCompanySettings,
	useRbacPrimaryRolePermissions,
	useSearchService,
	useSearchServicePrefetch,
} from "../../../../../api/hooks";
import { GOAL_TYPE, useBulkSelection } from "../../../../../hooks/useBulkSelection";
import { useFeatureFlagQuery } from "../../../../../hooks/useFeatureFlag";

// services
import { SERVICE_GOAL, getSearch } from "../../../../../api/services/defs/search-service";
import { Goal } from "../../../../../api/services/searchServiceResultTypes/goal";

// selectors
import { StateChange } from "../../../../../actions/page.actions";

import { getCsvDownloadJobID } from "./selectors";

// utils
import pubSub from "../../../../../utils/PubSub";
import { convertFiltersForBulkSelectionQuery, getGoalsListSearchServiceFilters } from "./utils";

// page template
import InfoPageTemplate from "../../../../../pageTemplates/InfoPageTemplate";
import EmptySearchTableMessage from "../../../../../pageTemplates/InfoPageTemplate/EmptySearchTableMessage";
import StandardToolbarActionRow from "../../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
// constants
import { GOALS_SIDE_NAVIGATION_LINKS } from "../../constants";
// css
import css from "./GoalsList.module.scss";
import config from "./config";

type GoalsListProps = {
	userID: string;
	userCanExportToCsv?: boolean;
	userCanBulkEditGoals?: boolean;
	navItems: Array<SideNavigationLink>;
};

const GoalsList = ({ userID, userCanBulkEditGoals = false, userCanExportToCsv = false, navItems }: GoalsListProps) => {
	const dispatch = useDispatch();
	const { data: companySettings, isLoading: isCompanySettingsLoading } = useCompanySettings();
	const { data: permissions } = useRbacPrimaryRolePermissions("goal");
	const goalWeightEnabled = Boolean(companySettings?.functions?.goals?.attributes["goalWeights"]);
	const { value: groupsFeatureVisible } = useFeatureFlagQuery("ff-is-groups-enabled", false);
	const groupsEnabled = Boolean(companySettings?.functions?.groups?.state === "enabled" && groupsFeatureVisible);

	const canUserCreateGoals = permissions?.includes("goals:createGoal");

	const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
	const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
	const [isBulkSelectionActive, setIsBulkSelectionActive] = useState(false);
	const [isFiltersOpen, setIsFiltersOpen] = useState(false);

	const goalWizardBaseUrl = "/goal/wizard/";
	const goalWizardUrlParams = `assignee=${userID}`;

	const downloadCsvJobID = useSelector((state) => getCsvDownloadJobID(state));

	const {
		pageNumber,
		setPageNumber,
		sort,
		setSort,
		filters,
		setFilters,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
	} = useInfoPageTemplateQueryState<GoalsFilters>({
		defaultSortName: "end_date",
		defaultSortDirection: "desc",
		defaultPageNumber: 1,
		fallbackFilters: {
			status: ["active"],
			transparency: ["public"],
		},
	});

	const {
		data,
		isLoading,
		refetch: refetchGoals,
	} = useSearchService<Goal>({
		service: SERVICE_GOAL,
		filters: getGoalsListSearchServiceFilters(filters, debouncedSearchValue),
		skip: (pageNumber - 1) * config.pageSize,
		limit: config.pageSize,
		sort,
		defaultErrorMessage: "There was an error getting Goals",
		disabled: isCompanySettingsLoading,
	});

	//prefetch page before and 3 pages after
	useSearchServicePrefetch(
		{
			service: SERVICE_GOAL,
			filters: getGoalsListSearchServiceFilters(filters, debouncedSearchValue),
			skip: (pageNumber - 2) * config.pageSize,
			limit: config.pageSize,
			sort,
			disabled: isCompanySettingsLoading,
		},
		pageNumber <= 1 ? 0 : 1
	);

	useSearchServicePrefetch(
		{
			service: SERVICE_GOAL,
			filters: getGoalsListSearchServiceFilters(filters, debouncedSearchValue),
			skip: pageNumber * config.pageSize,
			limit: config.pageSize,
			sort,
			disabled: isCompanySettingsLoading,
		},
		3
	);

	const goals = data?.results || [];
	const totalGoalsCount = data?.total || 0;

	const emptyTableState = <EmptySearchTableMessage mainText="No Goals Found" />;

	const {
		selections,
		add,
		remove,
		addQuery,
		clearAll,
		applyBulkUpdate,
		isQuerying,
		isInitialized,
		isMutationLoading,
		isBulkSelectionObjectLoading,
		isBulkSelecitonObjectRefetching,
		isApplyingBulkUpdate,
	} = useBulkSelection(GOAL_TYPE, {
		onBulkUpdateSuccess: () => refetchGoals(),
	});

	useEffect(() => {
		clearAll();
	}, [JSON.stringify(filters), debouncedSearchValue]);

	useEffect(() => {
		if (!isBulkSelectionActive && selections.length > 0) {
			clearAll();
		}
	}, [isBulkSelectionActive]);

	const handleDownloadCsv = async () => {
		const response = await getSearch(
			SERVICE_GOAL,
			getGoalsListSearchServiceFilters(filters, debouncedSearchValue),
			0,
			0,
			sort,
			true
		);

		const jobID = response?.jobID;
		pubSub.publish("check async frequency", { frequency: 500 });
		dispatch(StateChange(config.pageName, ["downloadCsvJobID"], jobID) as unknown as AnyAction);
	};

	const handleClearCsvDownload = () => {
		dispatch(StateChange(config.pageName, ["downloadCsvJobID"], null) as unknown as AnyAction);
	};

	const actionButtons = [
		userCanExportToCsv && (
			<DownloadButton
				autoDownload={true}
				jobID={downloadCsvJobID}
				isHollow={true}
				data-testid="downloadCSVButton"
				onClick={handleDownloadCsv}
				onDownload={handleClearCsvDownload}
			>
				Download CSV
			</DownloadButton>
		),
		canUserCreateGoals && (
			<Button key="create-goal" href={`${goalWizardBaseUrl}?${goalWizardUrlParams}`} icon={faPlus} color="green">
				Create Goal
			</Button>
		),
	];

	const secondaryActionButtons = [
		userCanBulkEditGoals && (
			<div className={css.bulkActionsSwitchContainer} key="bulk-actions-toggle">
				<div className={css.bulkActionsSwitchRow}>
					<Typography size="paragraph-3" weight="regular">
						Bulk Actions:
					</Typography>
					<div className={css.bulkActionsSwitch}>
						<ThumbSwitch
							value={isBulkSelectionActive}
							onChange={(value) => setIsBulkSelectionActive(value)}
						/>
					</div>
				</div>
			</div>
		),
	];

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={setDebouncedSearch}
			onFilterButtonPress={() => setIsFiltersOpen(!isFiltersOpen)}
			activeFiltersCount={Object.keys(filters).length}
			searchPlaceholder="Search Goals..."
		/>
	);

	const toolbarInformationRow = (
		<ReadableGoalsFilterChips filters={filters} setFilters={(newFilters) => setFilters(newFilters)} />
	);

	const drawer = (
		<GoalsListFilters
			isOpen={isFiltersOpen}
			onClose={() => setIsFiltersOpen(false)}
			filters={filters}
			onApplyFilters={(newFilters) => setFilters(newFilters as any)}
		/>
	);

	const tableActionButtons = [
		<Button
			key="completeButton"
			disabled={selections.length < 1 || isQuerying || !isInitialized}
			onClick={() => setIsCompleteModalOpen(true)}
			isHollow={true}
			icon={faCheck}
		>
			Complete
		</Button>,
		<Button
			key="archiveButton"
			disabled={selections.length < 1 || isQuerying || !isInitialized}
			onClick={() => setIsArchiveModalOpen(true)}
			isHollow={true}
			icon={faArchive}
		>
			Archive
		</Button>,
	];

	const goalIDs = goals.map((goal) => goal._id);
	const TABLE = userCanBulkEditGoals ? TableBulk : TableScrollable;

	const mainContent = (
		<div>
			<TABLE
				isAnimated={true}
				isBulkSelectionEnabled={isBulkSelectionActive}
				isLoading={isLoading || isApplyingBulkUpdate || isCompanySettingsLoading}
				rows={goals || []}
				columns={getGoalsListCols(isBulkSelectionActive, goalWeightEnabled, groupsEnabled)}
				activeSort={sort.name}
				activeSortDirection={sort.direction}
				onColumnClick={(columnID, direction) =>
					setSort({ name: columnID, direction: direction as SortDirection })
				}
				onCheckboxChange={(row, isSelected) => (isSelected ? remove([row._id]) : add([row._id]))}
				isRowChecked={(row) => selections.includes(row._id)}
				numberOfSelectionsLabel={
					selections?.length > 0
						? `${selections.length} Goal${selections.length > 1 ? "s are" : " is"} selected`
						: undefined
				}
				selectAllActionLabel={`Select all ${totalGoalsCount} goal${totalGoalsCount > 1 ? "s" : ""}`}
				selectAllValue={goalIDs.every((goalID) => selections.includes(goalID)) && goalIDs.length > 0}
				shouldShowClearSelection={selections.length >= totalGoalsCount && totalGoalsCount > 0}
				clearAllSelections={() => clearAll()}
				onSelectAllClick={() => {
					addQuery(convertFiltersForBulkSelectionQuery(filters, searchValue));
				}}
				onSelectAllPageChange={(isSelected) => {
					const ids = goals.map((goal) => goal._id);
					if (isSelected) {
						add(ids);
					} else {
						remove(ids);
					}
				}}
				areBulkSelectionBoxesEnabled={!isQuerying}
				isBulkSuperSelectAllEnabled={
					!isQuerying ||
					!isBulkSelectionObjectLoading ||
					!isBulkSelecitonObjectRefetching ||
					!isMutationLoading
				}
				tableActionButtons={tableActionButtons}
				emptyChildren={emptyTableState}
			/>
			<Spacer height={2} />
			<Pagination
				pageCount={Math.ceil(totalGoalsCount / config.pageSize)}
				currentPage={pageNumber}
				onClick={(page: number) => setPageNumber(page)}
			/>
			{isArchiveModalOpen && (
				<ArchiveGoalModal
					numberOfGoals={selections?.length}
					onClose={() => setIsArchiveModalOpen(false)}
					onUpdate={async () => {
						await applyBulkUpdate({ status: "archived" });
						refetchGoals();
					}}
				/>
			)}
			{isCompleteModalOpen && (
				<CompleteGoalsModal
					numberOfGoals={selections?.length}
					onClose={() => setIsCompleteModalOpen(false)}
					onUpdate={async (message) => {
						await applyBulkUpdate({ status: "done", message });
					}}
				/>
			)}
		</div>
	);

	return (
		<InfoPageTemplate
			actionButtons={actionButtons}
			secondaryActionButtons={secondaryActionButtons}
			toolbarActionRow={toolbarActionRow}
			toolbarInformationRow={toolbarInformationRow}
			drawer={drawer}
			mainContent={mainContent}
			pageTitle="Company Goals"
			navigationLinks={navItems}
			activeNavigationLinkKey={GOALS_SIDE_NAVIGATION_LINKS[0].key}
		/>
	);
};

export default GoalsList;
